import { IconX } from '@developerz/ui';
import { createSignal, For, type JSX } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Button } from './ui/Button';

// The donor's model CEILING as chips (arc 101 slice 11, plan doc 05) — the
// `models` list that rides a donated key.
//
// It starts at `*` because that is the gift most donors mean: "run whatever my
// endpoint serves". A concrete list is the tighter promise, and the maintainer
// picks strictly WITHIN whatever is here (`PATCH /v1/donations/:id/model`, 422
// outside it) — so a chip removed here is capacity the recipient can no longer
// choose, which is why removal is explicit and never a side effect of typing.
//
// Presenter: the parent owns the list and the submit. Duplicates and blanks are
// dropped on the way in rather than refused, because a donor pasting the same
// model twice meant one entry, not an error.

export type ModelChipsFieldProps = {
  readonly id: string;
  readonly models: readonly string[];
  readonly onChange: (models: readonly string[]) => void;
};

export function ModelChipsField(props: ModelChipsFieldProps): JSX.Element {
  const [draft, setDraft] = createSignal('');

  const add = (): void => {
    const value = draft().trim();
    if (value === '' || props.models.includes(value)) {
      setDraft('');
      return;
    }
    props.onChange([...props.models, value]);
    setDraft('');
  };

  // Enter adds a chip; it must not submit the surrounding donate form, which
  // would post a ceiling the donor was still typing.
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    add();
  };

  return (
    <Field
      label={t('donations.key_models_label')}
      id={props.id}
      desc={t('donations.key_models_hint')}
      error={props.models.length === 0 ? t('donations.models_empty') : null}
    >
      {(a11y) => (
        <>
          <ul class="chip-list">
            <For each={props.models}>
              {(model) => (
                <li class="badge badge--outline">
                  <span class="data-table__mono">{model}</span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('donations.models_remove', { model })}
                    onClick={() => props.onChange(props.models.filter((m) => m !== model))}
                  >
                    <IconX size="0.75rem" />
                  </Button>
                </li>
              )}
            </For>
          </ul>
          <div class="filter-row">
            <input
              {...a11y}
              class="input"
              value={draft()}
              placeholder={t('donations.models_placeholder')}
              onInput={(e) => setDraft(e.currentTarget.value)}
              onKeyDown={onKeyDown}
            />
            <Button variant="outline" size="sm" onClick={add}>
              {t('donations.models_add')}
            </Button>
          </div>
        </>
      )}
    </Field>
  );
}
